import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthUserInfo, IAuthService } from '@core-features/core';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import {
  BehaviorSubject,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  ReplaySubject,
} from 'rxjs';
import {
  AuthErrorPresenter,
  AuthHostServiceConfig,
  AuthLogger,
} from './models';
import {
  AUTH_HOST_ERROR_PRESENTER,
  AUTH_HOST_LOGGER,
  AUTH_HOST_SERVICE_CONFIG,
} from './tokens';
import { OidcConfig } from './utils';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Auth service for native (Capacitor) apps.
 *
 * Diverges from `AuthHostService` in three places:
 * 1. No iframe-based silent refresh — uses `refreshToken()` directly.
 * 2. No callback-route redirect (native deep-link replaces it).
 * 3. Exposes `setOpenUri` so the host app can route OAuth flows through
 *    an InAppBrowser instead of the system browser.
 */
@Injectable()
export class AuthMobileService implements IAuthService {
  private readonly _authLoggerService: AuthLogger = inject(AUTH_HOST_LOGGER);
  private readonly _authErrorPresenter: AuthErrorPresenter = inject(
    AUTH_HOST_ERROR_PRESENTER,
  );
  private readonly _authServiceConfig: AuthHostServiceConfig = inject(
    AUTH_HOST_SERVICE_CONFIG,
  );
  private readonly _ngZone: NgZone = inject(NgZone);
  private readonly _router: Router = inject(Router);
  private readonly _oauthService: OAuthService = inject(OAuthService);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  private readonly _state$ = new BehaviorSubject<
    'authenticating' | 'authenticated' | 'unauthenticated'
  >('authenticating');
  private readonly _userInfo$ = new ReplaySubject<AuthUserInfo | undefined>(1);
  private readonly _isTriggeredRefreshRunning$ = new BehaviorSubject<boolean>(
    false,
  );

  private readonly _signOutRoute = '/special/signout';

  /**
   * The OIDC config last applied via `configure()`. Kept so `setOpenUri`
   * can re-configure with `openUri` merged in, instead of reaching into the
   * protected `OAuthService.config` (the login/logout flows read
   * `config.openUri`, which has no public setter post-configure).
   */
  private _oidcConfig: AuthConfig = OidcConfig.Configuration({
    ...this._authServiceConfig,
    isMobileApp: true,
  });

  readonly isAuthenticated$: Observable<boolean> = this._state$.pipe(
    filter((state) => ['authenticated', 'unauthenticated'].includes(state)),
    map((state) => state === 'authenticated'),
  );
  readonly userInfo$: Observable<AuthUserInfo | undefined> =
    this._userInfo$.asObservable();

  readonly $isAuthenticated = toSignal(this.isAuthenticated$, {
    initialValue: false,
  });
  readonly $userInfo = toSignal(this.userInfo$);

  /**
   * Called by `provideEnvironmentInitializer` after DI is ready.
   * Configures OIDC for the native flow and bootstraps the session.
   */
  init() {
    this._oauthService.configure(this._oidcConfig);

    this._setupOAuthServiceEventsHandling();

    // Native bootstrap: read cached claims, then try a token refresh.
    // Happy path: refresh resolves -> `token_received` event -> `_getUserInfo`
    //   -> `_setAuthenticated(true)` with fresh user info.
    // Refresh fails: we still want the offline shell usable -> fall back to
    //   cached claims.
    // `_getUserInfo` (`loadUserProfile`) rejects: same fallback, otherwise the
    //   `_state$` would stay in 'authenticating' forever and the splash would
    //   hang. The `.finally()` watchdog guarantees we always exit the
    //   authenticating state. `_setAuthenticated`/`_userInfo$.next` are
    //   idempotent — if `_getUserInfo` later resolves it will overwrite
    //   cached info with fresh info.
    const cachedClaims = this._oauthService.getIdentityClaims();
    if (cachedClaims) {
      this._oauthService
        .refreshToken()
        .catch(() => {
          this._authLoggerService.logWarning(
            'Token refresh on init failed; falling back to cached claims.',
          );
        })
        .finally(() => {
          if (this._state$.value === 'authenticating') {
            this._userInfo$.next(cachedClaims as AuthUserInfo);
            this._setAuthenticated(true);
          }
        });
    } else {
      this._setAuthenticated(false);
    }

    this._oauthService?.setupAutomaticSilentRefresh();
  }

  /**
   * Allows the host app to redirect OAuth flows through an InAppBrowser
   * (Capacitor `InAppBrowser` plugin) instead of the system browser.
   * Not part of `IAuthService` — callers must inject the concrete class.
   *
   * Idempotency contract: callers should treat this as a one-time bind during
   * app bootstrap (palcode wires it once in `MobileSetupService`). Calling it
   * repeatedly is technically safe — session checks are off for mobile and
   * the refresh timer setup is idempotent — but `OAuthService.configure()`
   * re-runs `configChanged()` internally, which on some `angular-oauth2-oidc`
   * versions can race with an in-flight `refreshToken()`. If a future caller
   * needs to rebind dynamically, guard with a "first call wins" check and
   * cover it with a spec.
   */
  setOpenUri(openUri: (uri: string) => void): void {
    // `openUri` only takes effect through the AuthConfig, so re-run configure
    // with it merged.
    this._oidcConfig = { ...this._oidcConfig, openUri };
    this._oauthService.configure(this._oidcConfig);
  }

  isAccessTokenValid(): boolean {
    return this._oauthService.hasValidAccessToken();
  }

  async getAccessToken(): Promise<string> {
    const accessToken = this._oauthService.getAccessToken();
    // Refresh proactively 60s before expiry so in-flight requests don't
    // hit a 401 race on the first call after expiration.
    const shouldRefreshToken =
      accessToken &&
      Date.now() > this._oauthService.getAccessTokenExpiration() - 1000 * 60;

    if (shouldRefreshToken) {
      return this._tryRefreshSession().then(() =>
        this._oauthService.getAccessToken(),
      );
    }
    return accessToken;
  }

  login() {
    this._authLoggerService.logTrace('Start login.');
    this._oauthService.initCodeFlow();
  }

  /**
   * Exchanges the authorization code for tokens after the OAuth provider
   * redirects back to the app. On native, the host app (e.g. palcode's
   * `MobileSetupService`) intercepts the `/callback` URL inside an
   * InAppBrowser, rewrites the location so `tryLogin()` can read the params,
   * then calls this method.
   *
   * @returns `true` when tokens were obtained, `false` on any error.
   */
  async requestTokensIfRedirectFromAuthorizationServer(): Promise<boolean> {
    return this._oauthService.tryLogin().catch((error) => {
      this._authLoggerService.logError(
        'Error from oauthService.tryLogin().',
        error,
      );
      return false;
    });
  }

  logoutLocally(): void {
    this._oauthService.logOut(true);
    this._setAuthenticated(false);
  }

  logout(): void {
    this._authLoggerService.logTrace('Start logoff.');
    this._oauthService.logOut();
    this._setAuthenticated(false);
  }

  navigateToProfile(): void {
    window.open(
      this._authServiceConfig.securityTokenService + '/profile',
      '_blank',
      'noopener,noreferrer',
    );
  }

  // Re-fetches the profile from identity: a successful token refresh raises
  // `token_received` → `_getUserInfo()` → fresh immutable user via `userInfo$`.
  refreshUserInfo(): Observable<boolean> {
    return from(this._tryRefreshSession()).pipe(map(() => true));
  }

  navigateToSignOutPage() {
    const location = window.location.pathname + window.location.search;
    this._authLoggerService.logTrace(
      `Attempting navigation to sign-out page from: ${location}`,
    );

    if (location !== this._signOutRoute) {
      this._ngZone.run(() => {
        this._router.navigate([this._signOutRoute]).catch((error) => {
          this._authLoggerService.logWarning(
            'Navigation to sign-out page failed:',
            error,
          );
        });
      });
    }
  }

  private _setAuthenticated(authenticated: boolean): void {
    this._state$.next(authenticated ? 'authenticated' : 'unauthenticated');
  }

  private _setupOAuthServiceEventsHandling(): void {
    this._oauthService.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((e) => e.type === 'token_received'),
      )
      .subscribe(() => {
        this._authLoggerService.logTrace('Token received.');
        this._getUserInfo(!this.$isAuthenticated());
      });

    this._oauthService.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(
          (e): e is OAuthErrorEvent =>
            e.type === 'token_refresh_error' && e instanceof OAuthErrorEvent,
        ),
      )
      .subscribe((event) => {
        const status =
          event.reason instanceof HttpErrorResponse
            ? event.reason.status
            : undefined;
        if (this.$isAuthenticated() && status === 400) {
          this.navigateToSignOutPage();
        }
        this._authLoggerService.logWarning('Token refresh error.', event);
      });
  }

  private _getUserInfo(displayErrors = true): void {
    this._oauthService
      .loadUserProfile()
      .then((response) => {
        // `OAuthService.loadUserProfile()` returns `{ info, state }`. Narrow
        // the opaque `info` payload to our `AuthUserInfo` shape — same
        // pattern used in `AuthHostService._getUserInfo`.
        const info = (response as { info: AuthUserInfo }).info;
        this._authLoggerService.logTrace('User info loaded.');
        this._userInfo$.next(info);
        this._setAuthenticated(true);
      })
      .catch((error) => {
        this._authLoggerService.logError('Error loading user info', error);
        if (displayErrors) {
          this._authErrorPresenter.displayError();
        }
        // Never leave the session pinned in 'authenticating'. If a watchdog
        // (init() `.finally()`) already promoted state, this is a no-op.
        // For the login flow (token_received → profile fails), this is what
        // prevents the splash from hanging forever.
        if (this._state$.value === 'authenticating') {
          this._setAuthenticated(false);
        }
      });
  }

  /**
   * Tries to refresh tokens and user info.
   * It is used, when auth interceptor detects that access token is expired.
   * If the refresh fails with 'login_required', navigates to sign-out page.
   * If another error occurs, retries once.
   * If it still fails, the request which triggered this method will continue with the old token.
   * @return A promise which resolves when the refresh is completed or failed.
   */
  private async _tryRefreshSession(): Promise<void> {
    // Read+set must be synchronous to stay atomic. `firstValueFrom` resolves on
    // a microtask, so two concurrent callers would both read `false` before
    // either set `true` — producing two parallel `refreshToken()` calls. Using
    // the synchronous `.value` closes that race window.
    if (this._isTriggeredRefreshRunning$.value) {
      await firstValueFrom(
        this._isTriggeredRefreshRunning$.pipe(
          filter((isRunning) => !isRunning),
        ),
      );
      return;
    }

    this._isTriggeredRefreshRunning$.next(true);
    this._authLoggerService.logTrace('Start refresh login session (mobile).');

    try {
      await this._oauthService.refreshToken();
    } catch (error) {
      this._authLoggerService.logWarning('Mobile refresh error.', error);
      // 'token_refresh_error' event handler logs the detail; we do not
      // navigate to sign-out here because native deep-link recovery is
      // owned by the host app.
    } finally {
      this._isTriggeredRefreshRunning$.next(false);
    }
  }
}
